import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { getLayout, i18nIntl } from '../../lib/ep.js';

import GameContainer from './GameContainer.js';

import CrosswordsCanvas from '../games/CrosswordsCanvas.js';
import ActionButton, { SecondaryButton } from '../builder/ActionButton.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import ActionLink from '../builder/ActionLink.js';
import FloatingLayerPublic from './FloatingLayerPublic.js';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';

export const DownloadImagesLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_export_images_layer, setDisplayExportImagesLayer ] = useState(false);

	const is_builder = props?.is_builder ?? false;

	if(!is_builder) return null;

	const download = (is_solved = false) => {
		const specific = (is_solved) ? 'solved' : 'unsolved';
		const canvas = document.querySelector(`#${specific}_canvas canvas`);

		const link = document.getElementById(`download_${specific}_link`);
		link.setAttribute('download', `crosswords_${specific}.png`);
		link.setAttribute('href', canvas.toDataURL({ pixelRatio:3 }).replace(/^data:image\/[^;]/, 'data:application/octet-stream'));
		link.click();
	};

	return (
		<>
			<SecondaryButton
				onClick={() => setDisplayExportImagesLayer(true)}
			>Export to PNG</SecondaryButton>

			{(display_export_images_layer) && (
				<FloatingLayer
					_class={{ size:'small' }}
					backButton={() => setDisplayExportImagesLayer(false)}
				>
					<div class="without_actions">
						<SectionTitle>Export the crossword in PNG</SectionTitle>

						<SectionSubtitle>Download the images of the crossword to use them across your digital channels.</SectionSubtitle>

						<ul class="items_with_icon_selector gui_not_in_top">
							<li onClick={() => download()}>
								<span class="promo_type_icon gui_circled_icon mainTextIcon knk-number-1 no_bg">
									<span class="bg" style={{ outline:'1px solid', outlineOffset:'-2px' }} />
								</span>

								<span class="name" style={{ flexGrow:'1' }}>{i18n("Unsolved")}</span>

								<ButtonCollection>
									<a id="download_unsolved_link" style={{display:'none'}} />

									<ActionButton
										_class={{ other:'in_top gui_not_in_end_tiny' }}
									>Download</ActionButton>
								</ButtonCollection>
							</li>

							<li onClick={() => download(true)}>
								<span class="promo_type_icon gui_circled_icon mainTextIcon knk-number-2 no_bg">
									<span class="bg" style={{ outline:'1px solid', outlineOffset:'-2px' }} />
								</span>

								<span class="name" style={{ flexGrow:'1' }}>{i18n("Solved")}</span>

								<ButtonCollection>
									<a id="download_solved_link" style={{display:'none'}} />

									<ActionButton
										_class={{ other:'in_top gui_not_in_end_tiny' }}
									>Download</ActionButton>
								</ButtonCollection>
							</li>
						</ul>
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const GameHeader = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ used_reveals, setUsedReveals ] = useState(0);
	const [ display_reveal_layer, setDisplayRevealLayer ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	
	const stage = props?.stage?.Stage ?? {};
	const max_word_solves = stage?.crosswords_num_solves ?? 3;

	const revealWord = () => {
		setUsedReveals(used_reveals + 1);
		setDisplayRevealLayer(false);
		props.gameCanvas.current.revealWord();
	};

	let display_solve = props?.display_solve ?? false;
	if(used_reveals >= max_word_solves) display_solve = false;

	return (
		<div class="selected_word">
			<ActionLink
				icon='knk-arrow-l-big'
				_class={{ other:'word_nav prev_word' }}
				onClick={() => props.gameCanvas.current.prevWord()}
			/>

			<span class="word_definition">
				<span class="top_definition">{i18n("Click on a square")}</span>

				{(display_solve) && (
					<ActionLink
						_class={{ other:'gui_not_in_start_small' }}
						onClick={() => setDisplayRevealLayer(true)}
						autotranslate={false}
					>{`${i18n("Reveal")} (${used_reveals} / ${max_word_solves})`}</ActionLink>
				)}
			</span>

			<ActionLink
				icon='knk-arrow-r-big'
				_class={{ other:'word_nav next_word' }}
				onClick={() => props.gameCanvas.current.nextWord()}
			/>

			{(display_reveal_layer) && (
				<FloatingLayerPublic
					backButton={() => setDisplayRevealLayer(false)}
					is_builder={is_builder}
					enable_behavior={true}
					footer_buttons={<>
						<ActionButton
							_fullclass=''
							onClick={() => setDisplayRevealLayer(false)}
						>No, cancel</ActionButton>

						<ActionButton
							_fullclass=''
							onClick={revealWord}
						>Yes, reveal</ActionButton>
					</>}
				>
					<LeveledHTMLText>
						<h2>{i18n("Are you sure you want to reveal the word?")}</h2>

						<p>{props.intl.formatMessage({ id:"You have {num} attempts left." }, { num:max_word_solves - used_reveals })}</p>
					</LeveledHTMLText>
				</FloatingLayerPublic>
			)}
		</div>
	)
}

const CrosswordsHeader = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_give_up_layer, setDisplayGiveUpLayer ] = useState(false);

	const is_builder = props?.is_builder ?? false;

	return (
		<div class="game_header_buttons">
			<ButtonCollection _class={{ other:'flex_button' }}>
				<ActionButton
					onClick={() => setDisplayGiveUpLayer(true)}
					_fullclass='in_top'
				>Give Up</ActionButton>

				<ActionButton
					_fullclass='in_top'
					disabled={(!props.game.ready_to_sent) ? true : false}
					onClick={() => props.onEnded(true)}
				>Send</ActionButton>
			</ButtonCollection>

			{(display_give_up_layer) && (
				<FloatingLayerPublic
					backButton={() => setDisplayGiveUpLayer(false)}
					is_builder={is_builder}
					enable_behavior={true}
					footer_buttons={<>
						<ActionButton
							_fullclass=''
							onClick={() => setDisplayGiveUpLayer(false)}
						>No, cancel</ActionButton>

						<ActionButton
							_fullclass=''
							onClick={() => props.onEnded(false)}
						>Yes, give up</ActionButton>
					</>}
				>
					<LeveledHTMLText>
						<h2>{i18n("Are you sure you want to give up?")}</h2>

						<p>{i18n("Remember, challenges make us stronger. You can do it!")}</p>
					</LeveledHTMLText>
				</FloatingLayerPublic>
			)}
		</div>
	)
};

const CrosswordsClient = ({ stage, ...props }) => {
	const [ display_solve, setDisplaySolve ] = useState(false);

	const layout_props = getLayout(props?.promotion ?? {});
	const is_builder = props?.is_builder ?? false;
	const enable_export = (is_builder) ? (props?.enable_export ?? false) : false;

	useEffect(() => {
		stage.Stage.crosswords_positions = (stage.Stage?.crosswords_positions ?? []).map(word=>({...word,x:parseInt(word.x),y:parseInt(word.y)}));
	}, []);

	const displaySolveWord = () => {
		setDisplaySolve(true);
	};

	const hideSolveWord = () => {
		setDisplaySolve(false);
	};

	const final_stage = stage;
	if(props.actual_state != undefined) {
		final_stage.Stage = { ...final_stage.Stage};

		if(props.actual_state == 'success') {
			final_stage.Stage.display_status = 'success';
		} else if(props.actual_state == 'out_of_time') {
			final_stage.Stage.display_status = 'fail_time';
		} else if(props.actual_state == 'out_of_moves') {
			final_stage.Stage.display_status = 'fail_attempts';
		} else if(props.actual_state == 'fail') {
			final_stage.Stage.display_status = 'fail';
		} else {
			final_stage.Stage.display_status = 'partial_success';
		}
	}

	const is_touch_device = (('ontouchstart' in window) || (navigator.MaxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));

	return (
		<>
			<GameContainer
				{...props}
				stage={final_stage}
				instructions={false}
				game_type="stackit"
				display_attemps={false}
				display_score={false}
				display_audio_controls={false}
				auto_start_timer={false}
				instructions_has_border={false}
				instructions_classes='gui_flex_align_center'
				extra_top_content={<GameHeader
					stage={final_stage}
					promotion={props.promotion?.Promomotion ?? {}}
					display_solve={display_solve}
					is_builder={is_builder}
					intl={props.intl}
				/>}
				custom_header_content={<CrosswordsHeader
					stage={final_stage}
					is_builder={is_builder}
					intl={props.intl}
				/>}
				extra_content_above={(!is_builder && is_touch_device) ? <GameHeader
					stage={final_stage}
					promotion={props.promotion?.Promomotion ?? {}}
					display_solve={display_solve}
					is_builder={is_builder}
					intl={props.intl}
				/> : null}
				extra_content={<div id="word_definitions" />}
			>
				<CrosswordsCanvas
					promo_colors={layout_props.other}
					is_presolved={(is_builder) ? props.show_solved : false}
					locale={props.locale}
					display_solve_word={displaySolveWord}
					hide_solve_word={hideSolveWord}
				/>
			</GameContainer>

			{(enable_export) && (
				<>
					<div id="unsolved_canvas" style={{ display:'none' }}>
						<CrosswordsCanvas
							stage={final_stage.Stage}
							intl={props.intl}
							promo_colors={layout_props.other}
							locale={props.locale}
							is_builder={true}
							is_export={true}
						/>
					</div>

					<div id="solved_canvas" style={{ display:'none' }}>
						<CrosswordsCanvas
							stage={final_stage.Stage}
							intl={props.intl}
							promo_colors={layout_props.other}
							locale={props.locale}
							is_builder={true}
							is_presolved={true}
							is_export={true}
						/>
					</div>
				</>
			)}
		</>
	)
};

export default injectIntl(CrosswordsClient);